import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import EmailTriggersPluginPanel from "./plugins/EmailTriggersPluginPanel";
import UserMatrixPluginPanel from "./plugins/UserMatrixPluginPanel";
import UserProfileSettings from "./plugins/UserProfileSettings";
import FieldGroupingPluginPanel from "./plugins/FieldGroupingPluginPanel";
import CommercialGridDefaultsPanel from "./plugins/CommercialGridDefaultsPanel";
import ArchivedProjectsPanel from "./plugins/ArchivedProjectsPanel";
import ProjectCsvImportPanel from "./plugins/ProjectCsvImportPanel";
import ExpansionCsvImportPanel from "./plugins/ExpansionCsvImportPanel";
import CrmCsvImportPanel from "./plugins/CrmCsvImportPanel";
import CommercialDbResetPanel from "./plugins/CommercialDbResetPanel";
import ServiceOrderExportPanel from "./plugins/ServiceOrderExportPanel";
import AiSettingsPanel from "./plugins/AiSettingsPanel";
import MarketplaceSettingsPanel from "./plugins/MarketplaceSettingsPanel";
import ApiKeysSettingsPanel from "./plugins/ApiKeysSettingsPanel";
import { readStoredAdminFlag } from "./authAdmin";
import { fetchAppConfig, putAdminAppConfig } from "./plugins/appConfigApi";
import { canViewerPermission, getViewerEmailFromStorage, isImpersonating } from "./plugins/userMatrixStorage";
import { canAccessSettings } from "./plugins/appChromeAccess";
import { isPluginEnabled, PLUGIN_IDS } from "./plugins/registry";
import "./App.css";
import "./plugins/workflowSchedule.css";

const SETTINGS_TABS = [
  { id: "profile", label: "Profile", adminOnly: false },
  { id: "tools", label: "Tools", adminOnly: true },
  { id: "emails", label: "Emails", adminOnly: true },
  { id: "roles", label: "User Roles", adminOnly: true },
  { id: "tables", label: "Tables", adminOnly: true },
  { id: "archive", label: "Archive", adminOnly: true },
  { id: "apiKeys", label: "API Keys", adminOnly: true },
  { id: "ai", label: "AI", adminOnly: true, requiresAiPlugin: true },
  { id: "marketplace", label: "Marketplace", adminOnly: true, requiresMarketplacePlugin: true },
];

function SettingsPageBody() {
  const isAdmin = readStoredAdminFlag();
  const viewerEmail = getViewerEmailFromStorage();
  const canManagePlugins = canViewerPermission(viewerEmail, "canManagePlugins", true);
  const canManageUserMatrix = canViewerPermission(viewerEmail, "canManageUserMatrix", true);
  const effectiveAdmin = isAdmin && !isImpersonating() && (canManagePlugins || canManageUserMatrix);
  const [activeTab, setActiveTab] = useState("profile");
  const [signupEnabled, setSignupEnabled] = useState(true);
  const [authPolicyError, setAuthPolicyError] = useState("");
  const emailTriggersEnabled = isPluginEnabled(PLUGIN_IDS.EMAIL_TRIGGERS);
  const userMatrixEnabled = isPluginEnabled(PLUGIN_IDS.USER_MATRIX);
  const fieldGroupingEnabled = isPluginEnabled(PLUGIN_IDS.FIELD_GROUPING);
  const aiChatEnabled = isPluginEnabled(PLUGIN_IDS.AI_CHAT_PORTFOLIO);
  const marketplaceEnabled = isPluginEnabled(PLUGIN_IDS.MARKETPLACE);
  const crmEnabled = isPluginEnabled(PLUGIN_IDS.CRM);

  useEffect(() => {
    let cancelled = false;
    fetchAppConfig()
      .then((cfg) => {
        if (cancelled) return;
        setSignupEnabled(cfg?.authSettings?.signupEnabled !== false);
      })
      .catch((err) => {
        if (cancelled) return;
        setAuthPolicyError(err?.response?.data?.error || err?.message || "Could not load auth settings.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const tab = SETTINGS_TABS.find((t) => t.id === activeTab);
    if (tab?.adminOnly && !effectiveAdmin) {
      setActiveTab("profile");
    }
    if (activeTab === "emails" && !emailTriggersEnabled) setActiveTab("profile");
    if (activeTab === "roles" && !userMatrixEnabled) setActiveTab("profile");
    if (activeTab === "tables" && !fieldGroupingEnabled) {
      setActiveTab("profile");
    }
    if (activeTab === "ai" && (!aiChatEnabled || !effectiveAdmin)) {
      setActiveTab("profile");
    }
    if (activeTab === "marketplace" && (!marketplaceEnabled || !effectiveAdmin)) {
      setActiveTab("profile");
    }
  }, [
    activeTab,
    effectiveAdmin,
    emailTriggersEnabled,
    userMatrixEnabled,
    fieldGroupingEnabled,
    aiChatEnabled,
    marketplaceEnabled,
  ]);

  return (
    <div className="plugins-page plugins-page--settings">
      <div className="settings-page-inner">
      <div className="plugins-page-header">
        <h1 className="plugins-page-title">Settings</h1>
        <p className="plugins-page-lead">
          Profile and application settings live here.
        </p>
      </div>

      <nav className="settings-tabs" aria-label="Settings sections">
        {SETTINGS_TABS.filter((tab) => {
          if (tab.id === "tables" && !fieldGroupingEnabled) return false;
          if (tab.id === "emails" && !emailTriggersEnabled) return false;
          if (tab.id === "roles" && !userMatrixEnabled) return false;
          if (tab.id === "ai" && (!aiChatEnabled || !effectiveAdmin)) return false;
          if (tab.id === "marketplace" && (!marketplaceEnabled || !effectiveAdmin)) return false;
          return true;
        }).map((tab) => {
          const disabled = tab.adminOnly && !effectiveAdmin;
          return (
            <button
              key={tab.id}
              type="button"
              className={`settings-tab-btn${activeTab === tab.id ? " is-active" : ""}`}
              onClick={() => setActiveTab(tab.id)}
              disabled={disabled}
              aria-pressed={activeTab === tab.id}
            >
              {tab.label}
            </button>
          );
        })}
      </nav>

      <div className="settings-tab-panel">
      {activeTab === "profile" ? (
        <section className="plugins-card plugins-card-wide user-profile-settings-card">
          <UserProfileSettings />
        </section>
      ) : null}

      {activeTab === "tools" ? (
        effectiveAdmin ? (
          <>
            <ServiceOrderExportPanel />
            <CommercialDbResetPanel />
            <ProjectCsvImportPanel />
            <ExpansionCsvImportPanel />
            {crmEnabled ? <CrmCsvImportPanel /> : null}

            <section className="plugins-card plugins-card-wide" style={{ padding: "1rem 1.25rem", marginBottom: "1rem" }}>
              <h3 className="plugins-card-name" style={{ marginTop: 0 }}>Authentication</h3>
              <p className="plugins-card-desc">
                Control whether the login page allows new user signup.
              </p>
              <label className="plugins-toggle">
                <input
                  type="checkbox"
                  checked={signupEnabled}
                  onChange={async (e) => {
                    const next = e.target.checked;
                    setAuthPolicyError("");
                    setSignupEnabled(next);
                    try {
                      await putAdminAppConfig({ authSettings: { signupEnabled: next } });
                    } catch (err) {
                      setSignupEnabled(!next);
                      setAuthPolicyError(
                        err?.response?.data?.error || err?.message || "Could not save signup setting."
                      );
                    }
                  }}
                />
                <span>{signupEnabled ? "Signup enabled" : "Signup disabled"}</span>
              </label>
              {authPolicyError ? <p className="email-triggers-muted">{authPolicyError}</p> : null}
            </section>
          </>
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators can access Tools settings.</p>
          </div>
        )
      ) : null}

      {activeTab === "emails" ? (
        emailTriggersEnabled && effectiveAdmin && canManagePlugins ? (
          <section className="plugins-card plugins-card-wide">
            <EmailTriggersPluginPanel />
          </section>
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators with plugin access can manage email triggers.</p>
          </div>
        )
      ) : null}

      {activeTab === "roles" ? (
        userMatrixEnabled && effectiveAdmin && canManageUserMatrix ? (
          <section className="plugins-card plugins-card-wide">
            <UserMatrixPluginPanel />
          </section>
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators with User Matrix access can manage roles.</p>
          </div>
        )
      ) : null}

      {activeTab === "tables" ? (
        effectiveAdmin ? (
          <>
            <CommercialGridDefaultsPanel />
            <FieldGroupingPluginPanel />
          </>
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators can access table configuration.</p>
          </div>
        )
      ) : null}

      {activeTab === "archive" ? (
        effectiveAdmin ? (
          <ArchivedProjectsPanel />
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators can manage archived projects.</p>
          </div>
        )
      ) : null}

      {activeTab === "apiKeys" ? (
        effectiveAdmin ? (
          <ApiKeysSettingsPanel />
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">Only administrators can manage API keys.</p>
          </div>
        )
      ) : null}

      {activeTab === "ai" ? (
        aiChatEnabled && effectiveAdmin ? (
          <AiSettingsPanel />
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">
              Enable the Portfolio AI assistant plugin and sign in as an administrator to configure
              AI.
            </p>
          </div>
        )
      ) : null}

      {activeTab === "marketplace" ? (
        marketplaceEnabled && effectiveAdmin && canManagePlugins ? (
          <MarketplaceSettingsPanel />
        ) : (
          <div className="plugins-card plugins-card-wide">
            <p className="plugins-card-desc">
              Enable the Marketplace plugin and sign in as an administrator with plugin access to
              configure reward triggers.
            </p>
          </div>
        )
      ) : null}
      </div>

      <p className="plugins-page-footer">
        <Link to="/" className="project-detail-back">
          ← Back to board
        </Link>
      </p>
      </div>
    </div>
  );
}

export default function SettingsPage() {
  if (!canAccessSettings()) {
    return <Navigate to="/" replace />;
  }
  return <SettingsPageBody />;
}
